import { Controller } from "@hotwired/stimulus"

/**
 * Highlights the currently opened ticket row in the inbox list.
 *
 * Server-side rendering already marks the active row when the page loads
 * standalone (`/support/tickets/:id`). However, when a user navigates by
 * clicking another row, only the `ticket-detail` Turbo Frame is replaced —
 * the list partial is not re-rendered, so the active row class becomes
 * stale. This controller keeps the highlight in sync on click and on
 * Turbo navigation events.
 *
 * Wiring:
 *   data-controller="support--selected-ticket"     on the list container
 *   data-ticket-id="<id>"                          on each row root
 */
const ACTIVE_CLASSES = ["bg-primary/10"]
const INACTIVE_CLASSES = ["hover:bg-muted/50"]

export default class extends Controller {
  static targets = ["row"]

  connect() {
    this._syncFromUrl = this.syncFromUrl.bind(this)
    document.addEventListener("turbo:load", this._syncFromUrl)
    document.addEventListener("turbo:frame-load", this._syncFromUrl)
    this.syncFromUrl()
  }

  disconnect() {
    document.removeEventListener("turbo:load", this._syncFromUrl)
    document.removeEventListener("turbo:frame-load", this._syncFromUrl)
  }

  syncFromUrl() {
    const match = window.location.pathname.match(
      /\/support\/tickets\/([^/?#]+)/,
    )
    const activeId = match ? match[1] : null
    this.highlight(activeId)
  }

  highlight(activeId) {
    this.element.querySelectorAll("[data-ticket-id]").forEach((row) => {
      const link = row.querySelector("a[data-turbo-frame='ticket-detail']")
      if (!link) return
      const isActive = row.dataset.ticketId === activeId
      if (isActive) {
        link.classList.add(...ACTIVE_CLASSES)
        link.classList.remove(...INACTIVE_CLASSES)
        link.setAttribute("aria-current", "true")
      } else {
        link.classList.remove(...ACTIVE_CLASSES)
        link.classList.add(...INACTIVE_CLASSES)
        link.removeAttribute("aria-current")
      }
    })
  }
}
